import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// DS
import {
  Col,
  Row,
  VertTypography,
  VertButton,
  VertLoading,
  VertSearch,
  Divider,
  Spin,
} from '@vertem-core/vertice-ds';

// STYLES
import 'Assets/styles/default/components/Cart/CartSummaryStyles.less';

// UTILS
import { maskEmails } from 'Utils/maskFormat';
import { pointsMask } from 'Utils/commons/points';
import { onlyNumbers } from 'Utils/onlyNumbers';
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';
import generateUUID from 'Utils/generateUUID';
import CartCouponDiscount from '../CartCouponDiscount/CartCouponDiscount';
import { useLocation } from 'react-router-dom';
import ynToBool from 'Utils/ynToBool';
import { LoadingOutlined } from '@ant-design/icons';
import useGA from 'Utils/Ga';
import getNameOfVendor from 'Utils/getNameOfVendor';

// CART SUMMARY COMPONENT
const CartSummary = ({
  onClickButton,
  hasError,
  isLoading,
  hasItemsVirtualVoucher,
  buttonText,
  hideShipping,
  shippingFail
}) => {
  /* States */
  const [cepToSearch, setCepToSearch] = useState(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isCoupomDiscount, setIsCoupomDiscount] = useState(true);
  const [isValidCoupon, setIsValidCoupon] = useState(true);

  const { pathname } = useLocation();

  const isCartPage = pathname == '/cart' && buttonText == 'Ir para pagamento';
  const { eventGa } = useGA();

  /* Hooks */
  const Catalog = useSelector(({ Catalog: data }) => data);
  const itemsCartReducer = useSelector(({ Cart: data }) => data);

  const Participants = useSelector(({ Participants: data }) => data);
  const ShippingCart = useSelector(({ Shipping: data }) => data);
  const Stores = useSelector(({ Stores }) => Stores);

  const { callApi } = usePipeContext();

  /* Vars */
  const email = Participants?.email?.value || '';
  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;
  const { cart, coupon } = itemsCartReducer;
  const ShippingError = ShippingCart?.isError;
  const totalDiscount = coupon?.discountValue || 0;
  // const isValid = coupon?.isValid || false;

  const cartItems = cart?.items || [];
  const totalValue = cart.price;
  const totalShipping = ShippingCart?.value?.total;
  const hasShippingValue = totalShipping === 0 || totalShipping > 0;
  const isShippingFree = hasShippingValue && totalShipping === 0;
  const totalCartValue = totalValue + totalShipping - totalDiscount;
  const shippingError =
    ShippingCart && ShippingCart?.error && ShippingCart.error.subs;
  const cartError = itemsCartReducer && itemsCartReducer?.error;

  const hasShippingError = !!shippingError;
  const hasCartError = !!cartError;
  const replacementCEPIfParticipantHasNoCEP =
    sessionStorage.getItem('replacementCep');

  const formattedTotalItems = pointsMask(totalValue, decimalPlaces, pointsName);
  const formattedTotalShipping = pointsMask(
    totalShipping,
    decimalPlaces,
    pointsName
  );
  const formattedTotalCart = pointsMask(
    totalCartValue,
    decimalPlaces,
    pointsName
  );

  useEffect(() => {
    if (
      ynToBool(process.env.REACT_APP_HAS_COUPON) &&
      coupon &&
      !coupon.isValid &&
      isCartPage
    ) {
      setIsValidCoupon(false);
      return;
    }

    setIsValidCoupon(true);
  }, [coupon?.isValid]);

  useEffect(() => {
    if (!isLoading) {
      setGaVisualization();
    }
  }, [isLoading]);

  const setGaVisualization = () => {
    eventGa('view_cart', {
      currency: Catalog?.currencyConfiguration?.pointsName,
      value: parseFloat(itemsCartReducer?.cart?.price),
      items: itemsCartReducer?.cart?.items.map(
        ({ vendor, sku, name, price, quantity, category }) => ({
          item_id: sku,
          item_name: name,
          currency: Catalog?.currencyConfiguration?.pointsName?.toUpperCase(),
          item_brand: getNameOfVendor(vendor.id, vendor.type, Stores)?.name,
          item_category: category.name,
          price: parseFloat(price),
          quantity,
        })
      ),
    });
  };

  /* Utils */
  const totalCartItems = (items) => {
    if (items) {
      let total = 0;
      for (const item of items) {
        total += item.quantity;
      }
      return total;
    }
  };

  const fetchShippingCartWithNewZipCode = (zipCode) => {
    setCepToSearch(zipCode);
    const aliasProviderUUID = generateUUID();
    callApi({
      symbol: 'FETCH_CART_SHIPPING',
      alias: aliasProviderUUID,
      payload: {
        zipCode,
      },
    });

    sessionStorage.setItem('replacementCep', zipCode);
  };

  const clearAddressHandler = () => {
    setIsEditingAddress(true);
  };

  return (
    <Col className="cart-summary-wrapper">
      <Row className="cart-summary-title">
        <Col>
          <VertTypography.Title>Resumo do resgate</VertTypography.Title>
        </Col>
      </Row>
      <Col className="divider" />
      <Row className="cart-summary-items" justify="space-between">
        <Col>
          <VertTypography.Paragraph className="label">
            Itens ({totalCartItems(cartItems)})
          </VertTypography.Paragraph>
        </Col>
        <Col>
          <VertTypography.Paragraph>
            {`${formattedTotalItems.value} ${formattedTotalItems.label}`}
          </VertTypography.Paragraph>
        </Col>
      </Row>
      {totalDiscount > 0 && ynToBool(process.env.REACT_APP_HAS_COUPON) && (
        <>
          <Row className="cart-summary-total" justify="space-between">
            <Col>
              <VertTypography.Paragraph className="label">
                Desconto
              </VertTypography.Paragraph>
            </Col>
            <Col>
              <VertTypography.Paragraph>
                {`${
                  pointsMask(totalDiscount, decimalPlaces, pointsName).value
                } ${
                  pointsMask(totalDiscount, decimalPlaces, pointsName).label
                }`}
              </VertTypography.Paragraph>
            </Col>
          </Row>

          {!isCartPage && <Divider />}
        </>
      )}
      {isCoupomDiscount &&
        isCartPage &&
        ynToBool(process.env.REACT_APP_HAS_COUPON) && (
          <>
            <Divider />
            <CartCouponDiscount  isValidCoupon={isValidCoupon}/>
            <Divider />
          </>
        )}

      {!isEditingAddress &&  
      (
        <VertTypography.Paragraph
          className="change-address-link"
          onClick={clearAddressHandler}
          underline
          style={{
            visibility: ynToBool(process.env.REACT_APP_HIDE_FREIGHT) ? 'hidden' : ''
          }}
        >
          Editar endereço
        </VertTypography.Paragraph>
      )}

      {!hideShipping && (
        <Row className="cart-summary-shipping" justify="space-between"
        style={{
          display: ynToBool(process.env.REACT_APP_HIDE_FREIGHT) ? 'none' : ''
        }}
        >
          <Col>
            <VertTypography.Paragraph className="label">
              Frete
            </VertTypography.Paragraph>
          </Col>
          <Col>
            <VertTypography.Paragraph
              className={isShippingFree ? 'isFree' : ''}
            >
              {isLoading && (
                <Spin
                  indicator={
                    <LoadingOutlined spin className="cart-loading-spin" />
                  }
                  size="small"
                />
              )}

              {ShippingError && shippingFail && <span> CEP inválido <br/></span>}   

              {!isLoading &&
                hasShippingValue &&
                (isShippingFree ? (
                  <span>Grátis</span>
                ) : (
                  <span>{`${formattedTotalShipping.value} ${formattedTotalShipping.label}`}</span>
                ))}
            </VertTypography.Paragraph>
          </Col>
        </Row>
      )}

      {isEditingAddress && (
        <>
          <div className="shipping-input-wrapper">
            <VertSearch
              mask={[{ mask: '00000-000' }]}
              onButtonHandler={(e) => {
                fetchShippingCartWithNewZipCode(
                  e.currentTarget.value.replace('-', '')
                );
              }}
              onKeyPress={onlyNumbers}
              placeholder="00000-000"
              template="marketplace"
              value={replacementCEPIfParticipantHasNoCEP || cepToSearch}
            />

            <VertTypography.Paragraph className="shipping-search-link margin-top-extra-small">
              <a
                href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                target="_blank"
                rel="noreferrer"
              >
                Não sei meu CEP
              </a>
            </VertTypography.Paragraph>
          </div>
        </>
      )}

      {hasItemsVirtualVoucher && !!email && (
        <>
          <Col className="divider" />
          <Row className="cart-summary-shipping" justify="space-between">
            <Col>
              <VertTypography.Paragraph className="label">
                E-mail:
              </VertTypography.Paragraph>
            </Col>
            <Col>
              <VertTypography.Paragraph
                aria-label={`E-mail oculto: ${maskEmails(email)}`}
              >
                {maskEmails(email)}
              </VertTypography.Paragraph>
            </Col>
          </Row>
        </>
      )}
      <Col className="divider" />
      {hasShippingValue && (
        <Row className="cart-summary-total" justify="space-between">
          <Col>
            <VertTypography.Paragraph className="label">
              Subtotal
            </VertTypography.Paragraph>
          </Col>
          <Col>
            <VertTypography.Paragraph>
              {!hasShippingValue && !hideShipping && (
                <VertLoading size="small" />
              )}
              {!hideShipping &&
                hasShippingValue &&
                totalCartValue !== 0 &&
                `${formattedTotalCart.value} ${formattedTotalCart.label}`}

              {hideShipping &&
                totalCartValue !== 0 &&
                `${formattedTotalItems.value} ${formattedTotalItems.label}`}

              {totalCartValue === 0 && (
                <span className="cart-summary-total-free">Grátis</span>
              )}
            </VertTypography.Paragraph>
          </Col>
        </Row>
      )}
      <Col className="cart-summary-button padding-x-none">
        <VertButton
          type="detached"
          onClick={onClickButton}
          disabled={ hasError || isLoading || !isValidCoupon}
          loading={isLoading}
        >
          {buttonText}
        </VertButton>
      </Col>
    </Col>
  );
};
CartSummary.propTypes = {
  onClickButton: PropTypes.func,
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool,
  hasItemsVirtualVoucher: PropTypes.bool,
};

export default CartSummary;
